import React from 'react';
import { ThemeProvider } from '@mui/material/styles';
import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import Divider from '@mui/material/Divider';
import { theme, customStyles } from '../theme';

const sections = [
  {
    title: '1. Information We Collect',
    content: (
      <>
        <Typography variant="body1" color="text.secondary" paragraph>
          We collect the following types of information:
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
          {[
            <><strong>Account information</strong> — name and email address when you register or sign in with Google.</>,
            <><strong>Trip data</strong> — destinations, trip preferences, itineraries, and saved trips you create within the app.</>,
            <><strong>Usage data</strong> — how you interact with the app (pages visited, features used) to improve the Service.</>,
            <><strong>Device information</strong> — device type, operating system, and app version for diagnostic purposes.</>,
          ].map((item, i) => (
            <Box component="li" key={i} sx={{ mb: 1 }}>
              <Typography variant="body1" color="text.secondary">{item}</Typography>
            </Box>
          ))}
        </Box>
        <Typography variant="body1" color="text.secondary">
          We do not collect payment information, precise location data, or contact lists.
        </Typography>
      </>
    ),
  },
  {
    title: '2. How We Use Your Information',
    content: (
      <>
        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
          {[
            'To create and manage your account.',
            'To generate and save personalised travel itineraries.',
            'To improve and maintain the Service.',
            'To respond to support requests.',
            'To send essential service communications (e.g. account security emails).',
          ].map((item, i) => (
            <Box component="li" key={i} sx={{ mb: 1 }}>
              <Typography variant="body1" color="text.secondary">{item}</Typography>
            </Box>
          ))}
        </Box>
        <Typography variant="body1" color="text.secondary">
          We do not sell your personal information to third parties or use it for advertising purposes.
        </Typography>
      </>
    ),
  },
  {
    title: '3. Authentication',
    content: (
      <Typography variant="body1" color="text.secondary">
        You may sign in using Google OAuth. When you do, Google shares your name and email address with us.
        We store an authentication token on your device to keep you signed in. We do not store your Google password.
      </Typography>
    ),
  },
  {
    title: '4. Data Sharing',
    content: (
      <>
        <Typography variant="body1" color="text.secondary" paragraph>
          We share your data only in the following limited circumstances:
        </Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          {[
            <><strong>Service providers</strong> — third-party services that help us operate the platform (e.g. cloud hosting, mapping services). These providers are contractually bound to protect your data.</>,
            <><strong>Legal requirements</strong> — if required to do so by law or in response to valid legal process.</>,
            <><strong>Business transfer</strong> — in the event of a merger or acquisition, your data may be transferred to the new entity.</>,
          ].map((item, i) => (
            <Box component="li" key={i} sx={{ mb: 1 }}>
              <Typography variant="body1" color="text.secondary">{item}</Typography>
            </Box>
          ))}
        </Box>
      </>
    ),
  },
  {
    title: '5. Data Retention',
    content: (
      <Typography variant="body1" color="text.secondary">
        We retain your account and trip data for as long as your account is active. You may delete your account
        and associated data at any time by contacting us at{' '}
        <Link href="mailto:privacy@orionadventures.net" color="primary">
          privacy@orionadventures.net
        </Link>.
      </Typography>
    ),
  },
  {
    title: '6. Security',
    content: (
      <Typography variant="body1" color="text.secondary">
        We use industry-standard measures to protect your data, including encrypted storage of authentication
        tokens and HTTPS for all data transmission. No method of transmission over the internet is 100% secure,
        and we cannot guarantee absolute security.
      </Typography>
    ),
  },
  {
    title: "7. Children's Privacy",
    content: (
      <Typography variant="body1" color="text.secondary">
        The Service is not directed to children under the age of 13. We do not knowingly collect personal
        information from children. If you believe we have inadvertently collected such information, please
        contact us immediately.
      </Typography>
    ),
  },
  {
    title: '8. Third-Party Services',
    content: (
      <>
        <Typography variant="body1" color="text.secondary" paragraph>
          The Service uses the following third-party services, each with their own privacy policies:
        </Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <Box component="li">
            <Typography variant="body1" color="text.secondary">
              <Link href="https://policies.google.com/privacy" target="_blank" rel="noopener" color="primary">
                Google (OAuth, Maps)
              </Link>
            </Typography>
          </Box>
        </Box>
      </>
    ),
  },
  {
    title: '9. Your Rights',
    content: (
      <>
        <Typography variant="body1" color="text.secondary" paragraph>
          Depending on your location, you may have the right to:
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
          {[
            'Access the personal data we hold about you.',
            'Request correction of inaccurate data.',
            'Request deletion of your data.',
            'Object to or restrict certain processing of your data.',
          ].map((item, i) => (
            <Box component="li" key={i} sx={{ mb: 1 }}>
              <Typography variant="body1" color="text.secondary">{item}</Typography>
            </Box>
          ))}
        </Box>
        <Typography variant="body1" color="text.secondary">
          To exercise any of these rights, contact us at{' '}
          <Link href="mailto:privacy@orionadventures.net" color="primary">
            privacy@orionadventures.net
          </Link>.
        </Typography>
      </>
    ),
  },
  {
    title: '10. Changes to This Policy',
    content: (
      <Typography variant="body1" color="text.secondary">
        We may update this Privacy Policy from time to time. We will notify you of significant changes by
        updating the date at the top of this page. Continued use of the Service after changes constitutes
        acceptance of the updated policy.
      </Typography>
    ),
  },
  {
    title: '11. Contact Us',
    content: (
      <Typography variant="body1" color="text.secondary">
        If you have any questions about this Privacy Policy, please contact us at:{' '}
        <Link href="mailto:privacy@orionadventures.net" color="primary">
          privacy@orionadventures.net
        </Link>
      </Typography>
    ),
  },
];

export default function PrivacyPolicy() {
  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', flexDirection: 'column' }}>
        {/* Nav */}
        <AppBar position="sticky" elevation={0} sx={{
          background: customStyles.glass.background,
          backdropFilter: customStyles.glass.backdropFilter,
          WebkitBackdropFilter: customStyles.glass.backdropFilter,
          borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
          color: 'text.primary',
          zIndex: 1100,
        }}>
          <Toolbar>
            <Box
              component="a"
              href="/"
              sx={{
                flexGrow: 1,
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
                '&:hover': { opacity: 0.8 },
              }}
            >
              <img
                src="https://storage.googleapis.com/orion-trips-staging/images/logo/logo6.png"
                alt="Orion Adventures"
                style={{ height: 55, width: 'auto', objectFit: 'contain' }}
              />
            </Box>
            <Link
              href="/"
              sx={{
                color: 'primary.main',
                textDecoration: 'none',
                fontWeight: 600,
                fontSize: '1rem',
                '&:hover': { textDecoration: 'underline', color: 'primary.dark' },
              }}
            >
              Back to home
            </Link>
          </Toolbar>
        </AppBar>

        {/* Content */}
        <Container maxWidth="md" sx={{ py: { xs: 5, md: 8 }, flex: 1 }}>
          <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 800 }}>
            Privacy Policy
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 5 }}>
            Last updated: March 28, 2026
          </Typography>

          <Typography variant="body1" color="text.secondary" paragraph>
            Orion Adventures ("we", "our", or "us") is committed to protecting your privacy. This Privacy
            Policy explains how we collect, use, and safeguard your information when you use the Orion
            Adventures app and website (collectively, the "Service").
          </Typography>

          <Divider sx={{ my: 4 }} />

          {sections.map((section, i) => (
            <Box key={i} sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom sx={{ color: 'text.primary' }}>
                {section.title}
              </Typography>
              {section.content}
            </Box>
          ))}
        </Container>

        {/* Footer */}
        <Box
          component="footer"
          sx={{
            borderTop: '1px solid',
            borderColor: 'divider',
            py: 3,
            textAlign: 'center',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            &copy; 2026 Orion Adventures. All rights reserved.
            {' · '}
            <Link href="/" color="primary" sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
              Back to Orion Adventures
            </Link>
          </Typography>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
